import api from './api';

const BASE_URL = '/video-discovery';

const videoDiscoveryService = {
  getCategories: async (useCache = true, language = null) => {
    try {
      const currentLanguage = language || localStorage.getItem('appLanguage') || 'ru';

      if (useCache) {
        const cacheKey = `videoCategoriesCache_${currentLanguage}`;
        const cacheTimeKey = `videoCategoriesCacheTime_${currentLanguage}`;

        const cachedCategories = localStorage.getItem(cacheKey);
        const cacheTimestamp = localStorage.getItem(cacheTimeKey);
        const currentTime = new Date().getTime();

        if (cachedCategories && cacheTimestamp &&
          (currentTime - parseInt(cacheTimestamp) < 24 * 60 * 60 * 1000)) {
          return JSON.parse(cachedCategories);
        }
      }

      const response = await api.get(`${BASE_URL}/categories`, {
        params: { language: currentLanguage }
      });

      const fullData = response.data;

      const cacheKey = `videoCategoriesCache_${currentLanguage}`;
      const cacheTimeKey = `videoCategoriesCacheTime_${currentLanguage}`;

      localStorage.setItem(cacheKey, JSON.stringify(fullData));
      localStorage.setItem(cacheTimeKey, new Date().getTime().toString());

      return fullData;
    } catch (error) {
      const currentLanguage = language || localStorage.getItem('appLanguage') || 'ru';
      const cacheKey = `videoCategoriesCache_${currentLanguage}`;

      const cachedCategories = localStorage.getItem(cacheKey);
      if (cachedCategories) {
        return JSON.parse(cachedCategories);
      }
      throw error;
    }
  },

  getTopVideos: async ({
    category,
    subcategory = null,
    format = null,
    tags = [],
    region = null,
    limit = 20,
    offset = 0,
    rankingMethod = 'views',
    language = null
  }) => {
    try {
      const currentLanguage = language || localStorage.getItem('appLanguage') || 'ru';

      const params = {
        category,
        limit,
        offset,
        ranking: rankingMethod,
        language: currentLanguage
      };

      if (subcategory) {
        params.subcategory = subcategory;
      }

      if (region && region !== 'all') {
        params.region = region;
      }

      const formatToSend = format && format !== 'None' && format !== 'null' && format.trim() !== '' ? format : null;
      const tagsToSend = Array.isArray(tags) ? tags.filter(tag => tag && tag.trim() !== '') : [];

      if (formatToSend) {
        params.format = formatToSend;
      }

      if (tagsToSend.length > 0) {
        params.tags = tagsToSend.join(',');
      }

      const response = await api.get(`${BASE_URL}/top-videos`, { params });

      if (response.data && Array.isArray(response.data.videos)) {
        return response.data.videos;
      } else if (Array.isArray(response.data)) {
        return response.data;
      } else {
        return [];
      }
    } catch (error) {
      throw error;
    }
  },

  getTopVideosAll: async ({
    format = null,
    tags = [],
    region = null,
    limit = 20,
    offset = 0,
    rankingMethod = 'views',
    language = null
  }) => {
    try {
      const currentLanguage = language || localStorage.getItem('appLanguage') || 'ru';

      const params = {
        limit,
        offset,
        ranking: rankingMethod,
        all_categories: true,
        language: currentLanguage
      };

      if (region && region !== 'all') {
        params.region = region;
      }

      const formatToSend = format && format !== 'None' && format !== 'null' && format.trim() !== '' ? format : null;
      const tagsToSend = Array.isArray(tags) ? tags.filter(tag => tag && tag.trim() !== '') : [];

      if (formatToSend) {
        params.format = formatToSend;
      }

      if (tagsToSend.length > 0) {
        params.tags = tagsToSend.join(',');
      }

      const response = await api.get(`${BASE_URL}/top-videos-all`, { params });

      if (response.data && Array.isArray(response.data.videos)) {
        return response.data.videos;
      } else if (response.data && Array.isArray(response.data)) {
        return response.data;
      } else {
        return [];
      }
    } catch (error) {
      try {
        return await videoDiscoveryService.searchVideos({
          query: tags.length > 0 ? tags.join(' ') : '',
          limit,
          offset,
          sortBy: rankingMethod
        });
      } catch (secondError) {
        return [];
      }
    }
  },

  getGlobalTopVideos: async ({
    categoryId = null,
    limit = 20,
    offset = 0,
    rankingMethod = 'views',
    timeWindow = 'week',
    format = null,
    tags = [],
    region = null,
    language = null
  }) => {
    try {
      const currentLanguage = language || localStorage.getItem('appLanguage') || 'ru';

      const params = {
        limit,
        offset,
        ranking: rankingMethod,
        time_window: timeWindow,
        region: region || 'all',
        language: currentLanguage
      };

      if (categoryId) {
        params.category_id = categoryId;
      }

      const formatToSend = format && format !== 'None' && format !== 'null' && format.trim() !== '' ? format : null;
      const tagsToSend = Array.isArray(tags) ? tags.filter(tag => tag && tag.trim() !== '') : [];

      if (formatToSend) {
        params.format = formatToSend;
      }

      if (tagsToSend.length > 0) {
        params.tags = tagsToSend.join(',');
      }
      
      const response = await api.get(`${BASE_URL}/global-top`, { params });

      if (response.data && Array.isArray(response.data.videos)) {
        return response.data.videos;
      } else if (response.data && Array.isArray(response.data)) {
        return response.data;
      } else {
        return [];
      }
    } catch (error) {
      throw error;
    }
  },

  getVideoDetails: async (videoId, sections = ['basic', 'metrics', 'technical', 'visual', 'categorization'], language = null) => {
    try {
      const currentLanguage = language || localStorage.getItem('appLanguage') || 'ru';

      const params = {
        language: currentLanguage
      };

      if (sections && sections.length > 0) {
        params.sections = sections.join(',');
      }

      const response = await api.get(`${BASE_URL}/video-details/${videoId}`, { params });

      return response.data || {};
    } catch (error) {
      throw error;
    }
  },

  getMultipleVideoDetails: async (videoIds, sections = ['basic', 'metrics', 'technical', 'visual', 'categorization'], language = null) => {
    try {
      const currentLanguage = language || localStorage.getItem('appLanguage') || 'ru';

      const requestData = {
        video_ids: videoIds,
        sections,
        language: currentLanguage
      };

      const response = await api.post(`${BASE_URL}/video-details-batch`, requestData);

      return response.data || { videos: {}, errors: {} };
    } catch (error) {
      throw error;
    }
  },

  compareVideos: async ({
    videoIds,
    metrics = ['views', 'likes', 'comments', 'engagement', 'duration', 'description_length', 'tags_count', 'age_limit', 'upload_age_days'],
    language = null
  }) => {
    try {
      const currentLanguage = language || localStorage.getItem('appLanguage') || 'ru';

      const response = await api.post(`${BASE_URL}/video-comparison`, {
        video_ids: videoIds,
        metrics,
        language: currentLanguage
      });
      return response.data || {};
    } catch (error) {
      throw error;
    }
  },

  searchVideos: async ({
    query,
    limit = 20,
    offset = 0,
    sortBy = 'relevance',
    language = null
  }) => {
    try {
      const currentLanguage = language || localStorage.getItem('appLanguage') || 'ru';

      const params = {
        query,
        limit,
        offset,
        sort_by: sortBy,
        language: currentLanguage
      };

      const response = await api.get(`${BASE_URL}/search-videos`, { params });

      if (response.data && Array.isArray(response.data.videos)) {
        return response.data.videos;
      } else if (response.data && Array.isArray(response.data)) {
        return response.data;
      } else {
        return [];
      }
    } catch (error) {
      throw error;
    }
  },

  getTrendingVideos: async ({
    region = 'RU',
    categoryId = null,
    limit = 20,
    offset = 0,
    language = null
  }) => {
    try {
      const currentLanguage = language || localStorage.getItem('appLanguage') || 'ru';

      const params = {
        region,
        limit,
        offset,
        language: currentLanguage
      };

      if (categoryId) {
        params.category_id = categoryId;
      }

      const response = await api.get(`${BASE_URL}/trending`, { params });
      return response.data.videos || [];
    } catch (error) {
      throw error;
    }
  },

  getSimilarVideos: async ({
    videoId,
    limit = 10,
    language = null
  }) => {
    try {
      const currentLanguage = language || localStorage.getItem('appLanguage') || 'ru';

      const params = {
        video_id: videoId,
        limit,
        language: currentLanguage
      };

      const response = await api.get(`${BASE_URL}/similar-videos`, { params });
      return response.data || [];
    } catch (error) {
      throw error;
    }
  },

  getAvailableTags: async (language = null) => {
    try {
      const currentLanguage = language || localStorage.getItem('appLanguage') || 'ru';

      const response = await api.get(`${BASE_URL}/available-tags`, {
        params: { language: currentLanguage }
      });
      return response.data.tags || [];
    } catch (error) {
      return [
        'Music Video', 'Tutorial', 'Review', 'Vlog', 'Gameplay',
        'Technology', 'Comedy', 'Education', 'Travel', 'Cooking',
        'Science', 'Gaming', 'Interview', 'DIY', 'Reaction',
        'Programming', 'Sports', 'Podcast', 'Challenge', 'Unboxing',
        'Short Film', 'Animation', 'Documentary', 'Analysis', 'News',
        'Compilation', 'Cover Song', 'Trailer', 'Experiment', 'Walkthrough',
        'Motivation', 'Fitness', 'Fashion', 'Beauty', 'Food'
      ];
    }
  },

  getVideosByTags: async ({
    tags,
    limit = 20,
    offset = 0,
    language = null
  }) => {
    try {
      const currentLanguage = language || localStorage.getItem('appLanguage') || 'ru';

      const response = await api.post(`${BASE_URL}/videos-by-tags`, {
        tags,
        limit,
        offset,
        language: currentLanguage
      });

      return response.data.videos || [];
    } catch (error) {
      throw error;
    }
  },

  getNicheInsights: async ({
    category,
    subcategory = null,
    language = null
  }) => {
    try {
      const currentLanguage = language || localStorage.getItem('appLanguage') || 'ru';

      const params = {
        category,
        language: currentLanguage
      };

      if (subcategory) {
        params.subcategory = subcategory;
      }

      const response = await api.get(`${BASE_URL}/niche-insights`, { params });
      return response.data || {};
    } catch (error) {
      throw error;
    }
  },

  getVideoCategories: async (language = null) => {
    try {
      const currentLanguage = language || localStorage.getItem('appLanguage') || 'ru';

      const response = await api.get(`${BASE_URL}/video-categories`, {
        params: { language: currentLanguage }
      });
      return response.data.categories || [];
    } catch (error) {
      return [];
    }
  },

  clearCache: async (prefix = 'video_discovery', language = null) => {
    try {
      const currentLanguage = language || localStorage.getItem('appLanguage') || 'ru';

      const response = await api.post(`${BASE_URL}/clear-cache`, {
        prefix: prefix,
        language: currentLanguage
      });

      const cacheKey = `videoCategoriesCache_${currentLanguage}`;
      const cacheTimeKey = `videoCategoriesCacheTime_${currentLanguage}`;

      localStorage.removeItem(cacheKey);
      localStorage.removeItem(cacheTimeKey);

      return response.data;
    } catch (error) {
      throw error;
    }
  },

  searchVideoByUrl: async (url, language = null) => {
    try {
      const currentLanguage = language || localStorage.getItem('appLanguage') || 'ru';

      const params = {
        url: url.trim(),
        language: currentLanguage
      };

      const response = await api.get(`${BASE_URL}/search-by-url`, { params });
      
      return response.data || {};
    } catch (error) {
      throw error;
    }
  },

  extractVideoIdFromUrl: (url) => {
    if (!url || typeof url !== 'string') {
      return null;
    }

    try {
      const patterns = [
        /(?:youtube\.com\/watch\?v=)([a-zA-Z0-9_-]{11})/,
        /(?:youtu\.be\/)([a-zA-Z0-9_-]{11})/,
        /(?:youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/,
        /(?:youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
        /(?:m\.youtube\.com\/watch\?v=)([a-zA-Z0-9_-]{11})/
      ];

      for (const pattern of patterns) {
        const match = url.match(pattern);
        if (match && match[1] && match[1].length === 11) {
          return match[1];
        }
      }

      try {
        const urlObj = new URL(url);
        
        if (urlObj.hostname.includes('youtube.com') && urlObj.pathname === '/watch') {
          const videoId = urlObj.searchParams.get('v');
          if (videoId && videoId.length === 11) {
            return videoId;
          }
        }

        if (urlObj.hostname.includes('youtu.be')) {
          const videoId = urlObj.pathname.slice(1);
          if (videoId.length === 11) {
            return videoId;
          }
        }
      } catch (urlError) {
        // Ignore URL parsing errors
      }

      return null;
    } catch (error) {
      return null;
    }
  },

  isValidYouTubeUrl: (url) => {
    if (!url || typeof url !== 'string') {
      return false;
    }

    const videoId = videoDiscoveryService.extractVideoIdFromUrl(url);
    return videoId !== null;
  },

  normalizeYouTubeUrl: (url) => {
    if (!url) return url;

    const videoId = videoDiscoveryService.extractVideoIdFromUrl(url);
    if (videoId) {
      return `https://www.youtube.com/watch?v=${videoId}`;
    }

    return url;
  }
};

export default videoDiscoveryService;